/**
 * Mount this hook once (inside the app shell) so the user's stored
 * preferences are applied to the running UI on each login + page load.
 *
 * Source of truth:
 *   * language → `/me.preferences.language`  → i18n
 *   * theme    → `/me.preferences.theme`     → ThemeProvider
 *
 * Local toggles (Profile page, Topbar theme dropdown) write back to
 * the same endpoint so the choice persists across devices. localStorage
 * keeps working as a fast-path fallback while the network roundtrips.
 */
import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { setLanguage, type SupportedLanguage } from "@/i18n";
import { meApi } from "@/lib/api-me";
import { useTheme } from "@/theme/ThemeProvider";

export function usePreferencesSync(): void {
  const { setTheme } = useTheme();

  const query = useQuery({
    queryKey: ["me"],
    queryFn: () => meApi.get(),
    staleTime: 5 * 60 * 1000,
  });

  React.useEffect(() => {
    if (!query.data) return;
    const lang = query.data.preferences?.language;
    if (lang === "en" || lang === "it") {
      setLanguage(lang as SupportedLanguage);
    }
    const theme = query.data.preferences?.theme;
    if (theme === "light" || theme === "dark" || theme === "system") {
      setTheme(theme);
    }
  }, [query.data, setTheme]);
}
